import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ApiError,
  pushSitesApi,
  type MainTemplate,
  type PushSite,
  type PushSiteTemplate,
  type TemplatePushSite,
} from "@/api/client";
import { Download } from "lucide-react";
import { validateFieldMapping } from "@/adapters/pushProfiles";
import { Button } from "@/components/ui/button";
import { FieldMappingEditor } from "@/components/FieldMappingEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function PushBindingEditModal({
  binding,
  site,
  template,
  adapterType,
  showRemoteTemplate,
  saving,
  error,
  onSave,
  onClose,
}: {
  binding: TemplatePushSite;
  site: PushSite | undefined;
  template: MainTemplate | undefined;
  adapterType: string;
  showRemoteTemplate: boolean;
  saving?: boolean;
  error?: string | null;
  onSave: (body: {
    push_template_id?: number;
    field_mapping: string;
    models: string | null;
    model_mapping: string | null;
  }) => void;
  onClose: () => void;
}) {
  const [pushTemplateId, setPushTemplateId] = useState(String(binding.push_template_id));
  const [fieldMapping, setFieldMapping] = useState(binding.field_mapping ?? "");
  const [models, setModels] = useState<string | null>(binding.models);
  const [modelMapping, setModelMapping] = useState<string | null>(binding.model_mapping);
  const [remoteTemplates, setRemoteTemplates] = useState<PushSiteTemplate[]>([]);
  const [probeMsg, setProbeMsg] = useState<string | null>(null);

  const probeMut = useMutation({
    mutationFn: () => pushSitesApi.templates(binding.site_id),
    onSuccess: (r) => {
      const items = r.templates ?? [];
      setRemoteTemplates(items);
      setProbeMsg(r.ok ? `拉取到 ${items.length} 个远程模板` : (r.error ?? "拉取失败"));
    },
    onError: (e) => setProbeMsg(e instanceof ApiError ? e.message : "拉取失败"),
  });

  const selectedRemoteTemplate = remoteTemplates.find(
    (t) => String(t.id) === pushTemplateId,
  ) ?? null;
  const currentIdMissing =
    remoteTemplates.length > 0 &&
    pushTemplateId.trim() !== "" &&
    !remoteTemplates.some((t) => String(t.id) === pushTemplateId);

  function pickRemoteTemplate(idStr: string) {
    setPushTemplateId(idStr);
  }

  const validation = useMemo(
    () => validateFieldMapping(adapterType, fieldMapping),
    [adapterType, fieldMapping],
  );
  const nextPushTemplateId = Number(pushTemplateId);
  const pushTemplateIdValid =
    !showRemoteTemplate ||
    (pushTemplateId.trim() !== "" && Number.isInteger(nextPushTemplateId) && nextPushTemplateId >= 0);
  const dirty =
    (showRemoteTemplate && nextPushTemplateId !== binding.push_template_id) ||
    fieldMapping !== (binding.field_mapping ?? "") ||
    models !== binding.models ||
    modelMapping !== binding.model_mapping;
  const canSave =
    dirty && pushTemplateIdValid && validation.errors.length === 0 && !saving;

  return (
    <Modal
      title={`编辑绑定 · ${binding.site_name || `#${binding.site_id}`} × ${binding.template_name || template?.name || `#${binding.template_id}`}`}
      subtitle="模板与站点是绑定的联合主键，不可在此修改；如需换绑请先解绑再新增。"
      onClose={onClose}
      defaultSize="lg"
      sizeControls
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          const body: {
            push_template_id?: number;
            field_mapping: string;
            models: string | null;
            model_mapping: string | null;
          } = {
            field_mapping: fieldMapping.trim(),
            models,
            model_mapping: modelMapping,
          };
          if (showRemoteTemplate) body.push_template_id = Number(pushTemplateId) || 0;
          onSave(body);
        }}
      >
        {showRemoteTemplate ? (
          <div className="space-y-1">
            <Label>推送模板 ID（远程）</Label>
            <div className="flex gap-2">
              {remoteTemplates.length > 0 ? (
                <select
                  className={inpCls}
                  value={pushTemplateId}
                  onChange={(e) => pickRemoteTemplate(e.target.value)}
                >
                  <option value="">— 选择远程模板 —</option>
                  {remoteTemplates.map((rt) => (
                    <option key={rt.id} value={rt.id}>{rt.name} (#{rt.id})</option>
                  ))}
                  {currentIdMissing && (
                    <option value={pushTemplateId}>#{pushTemplateId}（当前值，不在拉取列表）</option>
                  )}
                </select>
              ) : (
                <Input
                  type="number"
                  min="0"
                  value={pushTemplateId}
                  onChange={(e) => setPushTemplateId(e.target.value)}
                  placeholder="远程模板 id（可先「拉取」）"
                />
              )}
              <Button
                type="button"
                size="sm"
                className="shrink-0 gap-1.5 font-semibold"
                disabled={probeMut.isPending || saving}
                onClick={() => probeMut.mutate()}
                title="拉取该推送站的远程模板列表"
              >
                <Download className="h-3.5 w-3.5" />
                {probeMut.isPending ? "拉取中…" : "拉取"}
              </Button>
            </div>
            {probeMsg && <p className="text-[11px] text-muted-foreground">{probeMsg}</p>}
            {selectedRemoteTemplate && (
              <pre className="mt-1 max-h-48 overflow-auto rounded-md border bg-muted/50 px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                {JSON.stringify(selectedRemoteTemplate, null, 2)}
              </pre>
            )}
            <p className="text-[11px] text-muted-foreground">
              下游推送站的远程模板 ID；可通过「拉取」选择，也可直接手填。选择模板仅更新 ID，不会自动覆盖字段映射。
            </p>
          </div>
        ) : (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            该适配器不需要远程模板 ID，直接编辑字段映射即可。
          </div>
        )}

        <div className="space-y-1">
          <Label>字段映射 field_mapping（JSON）</Label>
          <FieldMappingEditor
            adapterType={adapterType}
            value={fieldMapping}
            onChange={setFieldMapping}
          />
        </div>

        {validation.errors.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {validation.errors.join("; ")}
          </div>
        )}
        {validation.warnings.length > 0 && validation.errors.length === 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            ⚠ {validation.warnings.join("; ")}
          </div>
        )}

        <div className="rounded-lg border bg-muted/20 p-3">
          <InheritedModelConfigEditor
            models={models}
            modelMapping={modelMapping}
            inheritedModels={(site?.models ?? template?.models) || ""}
            inheritedModelMapping={(site?.model_mapping ?? template?.model_mapping) || ""}
            onModelsChange={setModels}
            onModelMappingChange={setModelMapping}
          />
        </div>

        {!pushTemplateIdValid && pushTemplateId.trim() !== "" && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            推送模板 ID 必须为 ≥ 0 的整数
          </div>
        )}
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={!canSave}>
            {saving ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
